import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './Simulation.css';

interface PreRead {
  title: string;
  description: string;
  url: string;
}

interface User {
  id: string;
  email: string;
  role: string;
}

interface Simulation {
  id: string;
  name: string;
  description: string;
  scenario: string;
  evaluation_criteria: any;
  ai_persona_config: any;
}

interface ConversationMessage {
  speaker: 'participant' | 'ai_agent';
  message: string;
  timestamp: Date;
  audioUrl?: string;
}

interface SimulationProps {
  user: User;
}

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

const Simulation: React.FC<SimulationProps> = ({ user }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [simulation, setSimulation] = useState<Simulation | null>(null);
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');
  const [objectivesMet, setObjectivesMet] = useState(false);
  const [prereadsOpen, setPrereadsOpen] = useState(true);
  const [coachingTip, setCoachingTip] = useState('');
  const [fairyMinimized, setFairyMinimized] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const conversationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSimulation();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [id]);

  useEffect(() => {
    // Auto-scroll to bottom of conversation
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversation]);

  const loadSimulation = async () => {
    try {
      const { data, error } = await supabase
        .from('simulations')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setSimulation(data);

      // Initialize WebSocket connection for real-time voice
      initializeWebSocket();

      // Fire opening intro for new users (up to 3 times total, across all simulations)
      const introKey = `sim_intro_count_${user.id}`;
      const introCount = parseInt(localStorage.getItem(introKey) || '0', 10);
      if (introCount < 3) {
        fireOpeningIntro(data.id);
        localStorage.setItem(introKey, String(introCount + 1));
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const fireOpeningIntro = async (simId: string) => {
    setIsProcessing(true);
    try {
      const response = await fetch('/api/voice/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: '',
          simulationId: simId,
          conversationHistory: [],
          isOpening: true,
        }),
      });
      const data = await response.json();
      if (data.success) {
        if (data.audio) playAudioResponse(data.audio);
        const OBJECTIVES_PREFIX = '[OBJECTIVES_MET]';
        let responseText: string = data.response;
        if (responseText.startsWith(OBJECTIVES_PREFIX)) {
          responseText = responseText.slice(OBJECTIVES_PREFIX.length).trimStart();
        }
        setConversation([{
          speaker: 'ai_agent',
          message: responseText,
          timestamp: new Date(),
        }]);
      }
    } catch {
      // Opening intro failure is non-fatal — session continues
    } finally {
      setIsProcessing(false);
    }
  };

  // Fire-and-forget coaching hint — non-blocking, failure is silent
  const fetchCoachingHint = (history: ConversationMessage[]) => {
    if (history.length < 2) return;
    fetch('/api/coaching-hint', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversationHistory: history.map(m => ({
          role: m.speaker === 'participant' ? 'user' : 'assistant',
          content: m.message,
        })),
      }),
    })
      .then(r => r.json())
      .then(data => { if (data.success && data.tip) setCoachingTip(data.tip); })
      .catch(() => {});
  };

  const initializeWebSocket = () => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${proto}//${window.location.host}/ws`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'ai_response') {
        // Replace the voice-processing placeholder with the real transcript
        if (data.userText) {
          setConversation(prev => {
            const updated = [...prev];
            const idx = updated.map(m => m.message).lastIndexOf('[Voice message - processing...]');
            if (idx !== -1) updated[idx] = { ...updated[idx], message: data.userText };
            return updated;
          });
        }

        const OBJECTIVES_PREFIX = '[OBJECTIVES_MET]';
        let responseText: string = data.response;
        if (responseText.startsWith(OBJECTIVES_PREFIX)) {
          responseText = responseText.slice(OBJECTIVES_PREFIX.length).trimStart();
          setObjectivesMet(true);
        }

        const newMessage: ConversationMessage = {
          speaker: 'ai_agent',
          message: responseText,
          timestamp: new Date(),
        };

        if (data.audio) {
          playAudioResponse(data.audio);
        }
        setConversation(prev => {
          const updated = [...prev, newMessage];
          fetchCoachingHint(updated);
          return updated;
        });
        setIsProcessing(false);
      }

      if (data.type === 'error') {
        setError(data.message || 'Voice processing error.');
        setIsProcessing(false);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      console.log('WebSocket disconnected');
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection error. Please refresh the page.');
    };
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        processAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error: any) {
      setError('Microphone access denied. Please allow microphone access and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob: Blob) => {
    setIsProcessing(true);

    // Add placeholder immediately so user sees feedback
    const participantMessage: ConversationMessage = {
      speaker: 'participant',
      message: '[Voice message - processing...]',
      timestamp: new Date(),
    };
    setConversation(prev => [...prev, participantMessage]);

    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Audio = reader.result as string;

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'voice_data',
            audio: base64Audio,
            simulationId: id,
            conversationHistory: conversation.map(msg => ({
              role: msg.speaker === 'participant' ? 'user' : 'assistant',
              content: msg.message,
            })),
          }));
        } else {
          setError('Voice connection lost. Please refresh the page.');
          setIsProcessing(false);
        }
      };
      reader.readAsDataURL(audioBlob);
    } catch (error: any) {
      setError('Error processing audio. Please try again.');
      setIsProcessing(false);
    }
  };

  const playAudioResponse = (audioBase64: string) => {
    try {
      // Received as raw base64 (no data URI prefix) — build a blob URL
      const binary = atob(audioBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const sendTextMessage = async () => {
    const input = document.getElementById('text-input') as HTMLInputElement;
    const message = input.value.trim();
    
    if (!message) return;

    // Add participant message
    const participantMessage: ConversationMessage = {
      speaker: 'participant',
      message,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, participantMessage]);

    // Send to backend for processing
    setIsProcessing(true);
    
    try {
      const response = await fetch('/api/voice/process', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          simulationId: id,
          conversationHistory: conversation.map(msg => ({
            role: msg.speaker === 'participant' ? 'user' : 'assistant',
            content: msg.message
          }))
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        const OBJECTIVES_PREFIX = '[OBJECTIVES_MET]';
        let responseText: string = data.response;
        if (responseText.startsWith(OBJECTIVES_PREFIX)) {
          responseText = responseText.slice(OBJECTIVES_PREFIX.length).trimStart();
          setObjectivesMet(true);
        }

        if (data.audio) {
          playAudioResponse(data.audio);
        }
        const aiMessage: ConversationMessage = {
          speaker: 'ai_agent',
          message: responseText,
          timestamp: new Date(),
        };
        setConversation(prev => {
          const updated = [...prev, aiMessage];
          fetchCoachingHint(updated);
          return updated;
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
      input.value = '';
    }
  };

  const endSimulation = async () => {
    if (conversation.length === 0) {
      alert('No conversation recorded. Please have a conversation before ending the simulation.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to end this simulation? This action cannot be undone.');
    if (!confirmed) return;

    try {
      // Get participant ID
      const { data: participant } = await supabase
        .from('participants')
        .select('id')
        .eq('user_id', user.id)
        .single();

      // Create transcript
      const transcript = conversation.map(msg => 
        `${msg.speaker === 'participant' ? 'Participant' : 'AI Agent'}: ${msg.message}`
      ).join('\n');

      // Send for analysis
      const response = await fetch('/api/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript,
          participantId: participant?.id,
          simulationId: id
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Navigate to results
        navigate(`/results/${data.resultId}`);
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  if (!simulation) {
    return (
      <div className="simulation-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading simulation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="simulation-container">
      <div className="simulation-header">
        <h1>{simulation.name}</h1>
        <div className="simulation-status">
          <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {isProcessing && <span className="processing-indicator">Processing...</span>}
        </div>
      </div>

      <div className="simulation-scenario">
        <h2>Scenario</h2>
        <p>{simulation.scenario}</p>
      </div>

      {/* Pre-reads panel */}
      <div className="prereads-panel">
        <button
          className="prereads-toggle"
          onClick={() => setPrereadsOpen(o => !o)}
          aria-expanded={prereadsOpen}
        >
          <span>📋 Prep Materials</span>
          <span className="prereads-chevron">{prereadsOpen ? '▲' : '▼'}</span>
        </button>
        {prereadsOpen && (
          <div className="prereads-body">
            {(simulation.ai_persona_config?.pre_reads as PreRead[] | undefined)?.length ? (
              <ul className="prereads-list">
                {(simulation.ai_persona_config.pre_reads as PreRead[]).map((doc, i) => (
                  <li key={i}>
                    {doc.url && doc.url !== '#'
                      ? <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.title}</a>
                      : <span className="prereads-title">{doc.title}</span>
                    }
                    {doc.description && <p>{doc.description}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="prereads-empty">No specific pre-reads assigned. Browse the resource library to prepare.</p>
            )}
            <a href="/library" className="prereads-library-link">Browse Resource Library →</a>
          </div>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      <div className="conversation-container" ref={conversationRef}>
        <div className="conversation-header">
          <h3>Conversation with {simulation.ai_persona_config?.name || 'AI Agent'}</h3>
        </div>
        
        <div className="conversation-messages">
          {conversation.length === 0 ? (
            <div className="conversation-empty">
              <p>Start the conversation by clicking the microphone button or typing a message below.</p>
            </div>
          ) : (
            conversation.map((message, index) => (
              <div key={index} className={`message ${message.speaker}`}>
                <div className="message-content">
                  <div className="message-header">
                    <span className="speaker">{message.speaker === 'participant' ? 'You' : simulation.ai_persona_config?.name || 'AI Agent'}</span>
                    <span className="timestamp">{message.timestamp.toLocaleTimeString()}</span>
                  </div>
                  <div className="message-text">{message.message}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {objectivesMet && (
        <div className="debrief-banner">
          <p>All objectives met. Ready to debrief?</p>
          <div className="debrief-actions">
            <button onClick={() => navigate(`/results/${id}`)}>View Results &amp; Debrief</button>
            <button className="debrief-continue" onClick={() => setObjectivesMet(false)}>Continue Practice</button>
          </div>
        </div>
      )}

      {/* Coaching fairy — appears after first AI response */}
      {coachingTip && (
        <div className={`coaching-fairy ${fairyMinimized ? 'minimized' : ''}`}>
          <div className="fairy-header">
            <span className="fairy-icon">✨</span>
            <span className="fairy-label">Coach</span>
            <button
              className="fairy-toggle"
              onClick={() => setFairyMinimized(m => !m)}
              aria-label={fairyMinimized ? 'Expand coach' : 'Minimize coach'}
            >
              {fairyMinimized ? '▲' : '▼'}
            </button>
          </div>
          {!fairyMinimized && (
            <p className="fairy-tip">{coachingTip}</p>
          )}
        </div>
      )}

      <div className="simulation-controls">
        <div className="voice-controls">
          <button
            className={`voice-button ${isRecording ? 'recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isProcessing}
          >
            {isRecording ? '🛑 Stop' : '🎤 Record'}
          </button>
        </div>

        <div className="text-controls">
          <input
            id="text-input"
            type="text"
            placeholder="Type your message..."
            onKeyPress={(e) => e.key === 'Enter' && sendTextMessage()}
            disabled={isProcessing}
          />
          <button onClick={sendTextMessage} disabled={isProcessing}>
            Send
          </button>
        </div>

        <div className="simulation-actions">
          <button 
            className="end-simulation-btn"
            onClick={endSimulation}
            disabled={isProcessing || conversation.length === 0}
          >
            End Simulation
          </button>
        </div>
      </div>
    </div>
  );
};

export default Simulation;
